import { useEffect, useState } from "react";
import { Loader2, ShieldCheck, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { errMsg } from "@/lib/api";

const inp = "w-full rounded-xl border border-input bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#FF7A00]";

export const NipVerified = ({ auth }) => (
  <div data-testid="nip-verified" className="rounded-xl bg-emerald-50 text-emerald-900 p-3 text-sm flex gap-2 items-start">
    <ShieldCheck size={18} className="shrink-0 mt-0.5" />
    <div><b>Autorización firmada con NIP</b>
      <div className="text-[11px] mt-0.5">Firma {auth.numero_firma} · {auth.verified_at ? new Date(auth.verified_at).toLocaleString("es-MX") : ""} · vía {auth.channel}{auth.folio_cdc || auth.cdc_audit?.folio ? ` · folio ${auth.folio_cdc || auth.cdc_audit?.folio}` : ""}{(auth.simulated ?? auth.cdc_audit?.simulated) ? " · simulado" : ""}</div>
    </div>
  </div>
);

export const NipAuth = ({ auth, onSend, onVerify, phone, onPhoneChange }) => {
  const [channel, setChannel] = useState("whatsapp");
  const [nip, setNip] = useState("");
  const [busy, setBusy] = useState(false);
  const [left, setLeft] = useState(0);
  const pending = auth?.status === "pendiente";
  useEffect(() => {
    if (!pending) return;
    const tick = () => setLeft(Math.max(0, Math.round((new Date(auth.expires_at) - Date.now()) / 1000)));
    tick(); const t = setInterval(tick, 1000); return () => clearInterval(t);
  }, [auth, pending]);
  const run = async (fn, ok) => { setBusy(true); try { await fn(); toast.success(ok); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  if (auth?.status === "verificado") return <NipVerified auth={auth} />;
  return (
    <div data-testid="nip-block" className="space-y-3">
      <div className="grid sm:grid-cols-[1fr_auto] gap-2 items-center">
        <input data-testid="nip-phone" className={inp} placeholder="Celular (10 dígitos)" value={phone || ""} onChange={(e) => onPhoneChange(e.target.value)} disabled={pending} />
        <div className="flex gap-1 text-xs">{["whatsapp", "sms"].map((c) => <button type="button" key={c} data-testid={`nip-channel-${c}`} onClick={() => setChannel(c)} className={`rounded-full px-3 py-2 font-semibold border ${channel === c ? "bg-[#0B0B0B] text-white border-[#0B0B0B]" : "bg-white border-border"}`}>{c === "whatsapp" ? "WhatsApp" : "SMS"}</button>)}</div>
      </div>
      {!pending ? (
        <button type="button" data-testid="nip-send" onClick={() => run(() => onSend(channel, phone), `NIP enviado por ${channel === "sms" ? "SMS" : "WhatsApp"}`)} disabled={busy || (phone || "").replace(/\D/g, "").length < 10} className="btn-brand w-full disabled:opacity-50">
          {busy ? <Loader2 className="animate-spin" size={15} /> : <Smartphone size={15} />} Enviar NIP a mi celular
        </button>
      ) : (
        <div className="space-y-2">
          <div className="flex gap-2">
            <input data-testid="nip-input" inputMode="numeric" maxLength={6} className={`${inp} tracking-[0.4em] text-center font-display text-lg`} placeholder="••••••" value={nip} onChange={(e) => setNip(e.target.value.replace(/\D/g, ""))} />
            <button type="button" data-testid="nip-verify" onClick={() => run(() => onVerify(nip), "Autorización firmada con NIP")} disabled={busy || nip.length !== 6} className="btn-brand shrink-0 disabled:opacity-50">{busy ? <Loader2 className="animate-spin" size={15} /> : "Verificar"}</button>
          </div>
          <div className="flex items-center justify-between text-[11px] text-[#8A8A8A]"><span>Enviado a {auth.phone_masked} · vence en {Math.floor(left / 60)}:{String(left % 60).padStart(2, "0")}</span><button type="button" data-testid="nip-resend" onClick={() => run(() => onSend(channel, phone), "NIP reenviado")} className="underline">Reenviar</button></div>
          {auth.dev_nip && <div data-testid="nip-dev-hint" className="text-[11px] rounded-lg bg-amber-50 text-amber-800 px-2 py-1.5">Modo simulado (sin WhatsApp/SMS real): tu NIP es <b className="font-mono tracking-widest">{auth.dev_nip}</b></div>}
        </div>
      )}
    </div>
  );
};

export const MX_STATES = ["AGS", "BC", "BCS", "CAMP", "CHIS", "CHIH", "CDMX", "COAH", "COL", "DGO", "GTO", "GRO", "HGO", "JAL", "MEX", "MICH", "MOR", "NAY", "NL", "OAX", "PUE", "QRO", "QROO", "SLP", "SIN", "SON", "TAB", "TAMPS", "TLAX", "VER", "YUC", "ZAC"];
